import baseStyleText from './as-button-base.css?raw';

/**
 * Shared base for all button components (as-button, as-text-button, as-icon-button).
 *
 * Provides:
 *   - _baseSheet: shared CSSStyleSheet (font-face, host defaults, size="s" scale var)
 *   - toggledOn state + forceToggleState()
 *   - click() delegation to the inner shadow <button>
 *   - _dispatchButtonClick() for consistent as-button-click event dispatch
 *   - _syncToggleState() hook for subclasses to update visual toggle state
 */
export abstract class AsButtonBase extends HTMLElement {
  static readonly _baseSheet = (() => {
    const s = new CSSStyleSheet();
    s.replaceSync(baseStyleText);
    return s;
  })();
  toggledOn = false;

  /** Programmatically click the inner shadow <button>. */
  click(): void {
    this.shadowRoot?.querySelector('button')?.click();
  }

  /** Force the toggle visual state. Only works when the `toggle` attribute is present. */
  forceToggleState(on: boolean): void {
    if (!this.hasAttribute('toggle')) return;
    this.toggledOn = on;
    this._syncToggleState();
  }

  /** Sync visual toggle state to `this.toggledOn`. Override in subclasses that support toggle. */
  protected _syncToggleState(): void {}

  /** Dispatch the standard as-button-click event. */
  protected _dispatchButtonClick(detail: {
    buttonName: string | null;
    toggledOn?: boolean;
    originalEvent?: MouseEvent;
  }): void {
    this.dispatchEvent(new CustomEvent('as-button-click', {
      bubbles: true,
      composed: true,
      detail,
    }));
  }
}
